import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Global } from './Global';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// Propósito: construir query strings sin superar límites de complejidad.
function appendParam(params: string[], name: string, value: any): void {
  if (value) params.push(`${name}=${value}`);
}

function buildQuery(params: string[]): string {
  return params.length ? '?' + params.join('&') : '';
}

function buildEntitySuffix(entity: any): string {
  return entity?.id ? '/' + entity.id : '';
}

function collectGoalParams(entityId?: string, year?: number): string[] {
  const params: string[] = [];
  appendParam(params, 'entityId', entityId);
  appendParam(params, 'year', year);
  return params;
}

function collectHistoryParams(params: { questionId?: string, entityId?: string, moduleId?: string, year?: number }): string[] {
  const out: string[] = [];
  appendParam(out, 'questionId', params.questionId);
  appendParam(out, 'entityId', params.entityId);
  appendParam(out, 'moduleId', params.moduleId);
  appendParam(out, 'year', params.year);
  return out;
}

function collectPeriodParams(moduleId?: string, groupId?: string): string[] {
  const params: string[] = [];
  appendParam(params, 'moduleId', moduleId);
  appendParam(params, 'groupId', groupId);
  return params;
}

@Injectable({ providedIn: 'root' })
export class EvaluationService {
  private _http = inject(HttpClient);
  private _url = Global.url_evaluation;
  private _headers = new HttpHeaders().set('Content-Type', 'application/json');

  getActiveModules() {
    return this._http.get(this._url + 'modules/active', { headers: this._headers });
  }

  getQuestionTypes(): Observable<any> {
    return this._http.get(this._url + 'question-types', { headers: this._headers });
  }

  upsertQuestionType(data: any): Observable<any> {
    return this._http.put(this._url + 'question-types', data, { headers: this._headers });
  }

  toggleQuestionType(id: string, enabled: boolean): Observable<any> {
    return this._http.patch(this._url + 'question-types/toggle', { id, enabled }, { headers: this._headers });
  }

  deleteQuestionType(id: string): Observable<any> {
    return this._http.delete(this._url + 'question-types/' + id, { headers: this._headers });
  }

  getPersonalGoals(moduleId: string, entityId?: string, year?: number): Observable<any> {
    const params = collectGoalParams(entityId, year);
    const qs = buildQuery(params);
    return this._http.get(this._url + `personal-goals/${moduleId}${qs}`, { headers: this._headers });
  }

  upsertPersonalGoal(goal: any): Observable<any> {
    return this._http.put(this._url + 'personal-goals', goal, { headers: this._headers });
  }

  deletePersonalGoal(id: string): Observable<any> {
    return this._http.delete(this._url + 'personal-goals/' + id, { headers: this._headers });
  }

  getPersonalGoalHistory(params: { questionId?: string, entityId?: string, moduleId?: string, year?: number }): Observable<any> {
    const query = collectHistoryParams(params);
    const qs = buildQuery(query);
    return this._http.get(this._url + 'personal-goals/history' + qs, { headers: this._headers });
  }

  upsertModule(data: any) {
    return this._http.put(this._url + 'modules', data, { headers: this._headers });
  }

  toggleModule(id: string, enabled: boolean) {
    return this._http.patch(this._url + 'modules/toggle', { id, enabled }, { headers: this._headers });
  }

  getCategories(moduleId: string, year: number, group: any) {
    return this._http.get(this._url + `categories/${moduleId}/${year}/${group.id}`, { headers: this._headers });
  }

  updateCategory(categories: any) {
    return this._http.put(this._url + 'categories', categories, { headers: this._headers });
  }

  deleteCategory(id: string) {
    return this._http.delete(this._url + 'categories/' + id, { headers: this._headers });
  }

  getQuestions(categoryId: string) {
    return this._http.get(this._url + `questions/${categoryId}`, { headers: this._headers });
  }

  updateQuestions(questions: any) {
    return this._http.put(this._url + 'questions', questions, { headers: this._headers });
  }

  deleteQuestion(id: string) {
    return this._http.delete(this._url + 'questions/' + id, { headers: this._headers });
  }

  deleteQuestionForce(id: string) {
    return this._http.delete(this._url + 'questions/' + id + '/force', { headers: this._headers });
  }

  toggleQuestionEnabled(id: string, enabled: boolean) {
    return this._http.patch(this._url + 'questions/' + id + '/toggle', { enabled }, { headers: this._headers });
  }

  deleteSubQuestion(id: string) {
    return this._http.delete(this._url + 'subquestions/' + id, { headers: this._headers });
  }

  deleteSubQuestionForce(id: string) {
    return this._http.delete(this._url + 'subquestions/' + id + '/force', { headers: this._headers });
  }

  toggleSubQuestionEnabled(id: string, enabled: boolean) {
    return this._http.patch(this._url + 'subquestions/' + id + '/toggle', { enabled }, { headers: this._headers });
  }

  getPreview(moduleId: string, group: any, year: number) {
    return this._http.get(this._url + `preview/${moduleId}/${group.id}/${year}`, { headers: this._headers });
  }

  getLatestPeriod() {
    return this._http.get(this._url + 'entity/latest', { headers: this._headers });
  }

  getLatestPeriodByModule(moduleId: string) {
    return this._http.get(this._url + `entity/latest/${moduleId}`, { headers: this._headers });
  }

  getMyPeriods(moduleId: string): Observable<any> {
    return this._http.get(this._url + `periods/my/${moduleId}`, { headers: this._headers });
  }

  getPeriodById(periodId: string): Observable<any> {
    return this._http.get(this._url + `entity/by-id/${periodId}`, { headers: this._headers });
  }

  getPeriod(period: string, entity: any, moduleId?: string, groupId?: string) {
    const suffix = buildEntitySuffix(entity);
    const params = collectPeriodParams(moduleId, groupId);
    const qs = buildQuery(params);
    return this._http.get(this._url + `entity/${period}${suffix}${qs}`, { headers: this._headers });
  }

  getPeriods(moduleId: string, period: string, group: any) {
    return this._http.get(this._url + `entities/${moduleId}/${period}/${group.id}`, { headers: this._headers });
  }

  listPeriodHistory(moduleId: string, groupId: string, year: number): Observable<any> {
    return this._http.get(this._url + `periods/history/${moduleId}/${year}/${groupId}`, { headers: this._headers });
  }

  closePeriods(ids: string[]): Observable<any> {
    return this._http.patch(this._url + 'periods/close', { ids }, { headers: this._headers });
  }

  resetEntityEvaluation(entityIds: string[], moduleId: string): Observable<any> {
    return this._http.post(this._url + 'periods/reset-entity', { entityIds, moduleId }, { headers: this._headers });
  }

  updatePeriod(id: string, score?: number, file?: string, expiration?: string) {
    return this._http.patch(this._url + 'periods/update', { id, score, file, expiration }, { headers: this._headers });
  }

  openPeriod(groupId: string, moduleId: string, expirationDate: string, period: string, entityId?: string, force?: boolean) {
    return this._http.post(this._url + 'periods/open', { group: groupId, moduleId, expirationDate, period, entityId, force }, { headers: this._headers });
  }

  finishPeriod(data: any) {
    return this._http.patch(this._url + 'periods/finish', data, { headers: this._headers });
  }

  signPeriod(data: any) {
    return this._http.patch(this._url + 'periods/sign', data, { headers: this._headers });
  }

  auditPeriod(data: any) {
    return this._http.patch(this._url + 'periods/audit', data, { headers: this._headers });
  }

  resetPeriod(periodId: string, expiration?: string) {
    return this._http.patch(this._url + 'periods/reset', { id: periodId, expiration }, { headers: this._headers });
  }

  updateAnswer(item: any) {
    return this._http.patch(this._url + 'answers', item, { headers: this._headers });
  }

  getLiveScore(periodId: string): Observable<any> {
    return this._http.get(this._url + `periods/${periodId}/live-score`, { headers: this._headers });
  }

  getYtdBase(periodId: string): Observable<any> {
    return this._http.get(this._url + `periods/${periodId}/ytd-base`, { headers: this._headers });
  }

  getFile(fileId: string): Observable<Blob> {
    return this._http.get(this._url + 'files/' + fileId, { headers: this._headers, responseType: 'blob' });
  }

  uploadFile(answer: any, formData: FormData): Observable<any> {
    formData.append('answer', JSON.stringify(answer));
    return this._http.post(this._url + 'files/upload', formData);
  }
}
